import { apiClient, ApiResponse } from './apiClient';
import { Permission } from './rolesService';

export interface PermissionFilters {
  search?: string;
  resource?: string;
  action?: string;
  status?: 'active' | 'inactive' | '';
}

export interface PermissionListResponse {
  permissions: Permission[];
  total: number;
  page: number;
  limit: number;
}

export interface CreatePermissionRequest {
  name: string;
  description: string;
  resource: string;
  action: string;
  isActive?: boolean;
}

export interface UpdatePermissionRequest {
  name?: string;
  description?: string;
  resource?: string;
  action?: string;
  isActive?: boolean;
}

export interface PermissionStats {
  total_permissions: number;
  active_permissions: number;
  inactive_permissions: number;
  resources: string[];
  actions: string[];
}

export class PermissionsService {
  private baseUrl = '/admin/permissions';

  // Get all permissions
  async getPermissions(filters?: PermissionFilters, page?: number, limit?: number): Promise<ApiResponse<Permission[]>> {
    try {
      console.log('🔧 PermissionsService.getPermissions called:', { filters, page, limit });
      
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.resource) params.append('resource', filters.resource);
      if (filters?.action) params.append('action', filters.action);
      if (filters?.status) params.append('status', filters.status);
      if (page) params.append('page', page.toString());
      if (limit) params.append('limit', limit.toString());
      
      const url = params.toString() ? `${this.baseUrl}?${params}` : this.baseUrl;
      console.log('🔧 Making GET request to:', url);
      
      const response = await apiClient.get<Permission[]>(url);
      
      console.log('🔧 PermissionsService.getPermissions response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PermissionsService.getPermissions error:', error);
      
      let errorMessage = 'Error fetching permissions';
      
      if (error.response?.status === 500 || error.response?.status === 404) {
        errorMessage = 'Permissions endpoint not available. Please ensure the backend API is running.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    }
  }

  // Get single permission
  async getPermission(id: number): Promise<ApiResponse<Permission>> {
    try {
      console.log('🔧 PermissionsService.getPermission called:', { id });
      
      const response = await apiClient.get<Permission>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 PermissionsService.getPermission response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PermissionsService.getPermission error:', error);
      
      let errorMessage = 'Error fetching permission';
      
      if (error.response?.status === 404) {
        errorMessage = 'Permission not found';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Create permission
  async createPermission(data: CreatePermissionRequest): Promise<ApiResponse<Permission>> {
    try {
      console.log('🔧 PermissionsService.createPermission called:', { data });
      console.log('🔧 Making POST request to:', this.baseUrl);
      console.log('🔧 Request payload:', JSON.stringify(data, null, 2));
      
      // Try different field name formats that the backend might expect
      const apiPayload = {
        name: data.name,
        description: data.description,
        resource: data.resource,
        action: data.action,
        isActive: data.isActive !== undefined ? data.isActive : true,
        // Also try snake_case versions
        is_active: data.isActive !== undefined ? data.isActive : true,
      };
      
      console.log('🔧 Final API payload:', JSON.stringify(apiPayload, null, 2));
      
      const response = await apiClient.post<Permission>(this.baseUrl, apiPayload);
      
      console.log('🔧 PermissionsService.createPermission response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PermissionsService.createPermission error:', error);
      console.error('🔧 Error response status:', error.response?.status);
      console.error('🔧 Error response data:', error.response?.data);
      console.error('🔧 Full error object:', JSON.stringify(error.response?.data, null, 2));
      
      // Special case: Check if the permission was actually created despite the error
      // This can happen when backend returns 422 but still creates the resource
      if (error.response?.status === 422 && error.response?.data) {
        // If the error response contains a valid permission object with an ID, treat it as success
        const responseData = error.response.data;
        if (responseData.id && responseData.name && responseData.resource && responseData.action) {
          console.log('🔧 Permission was created despite 422 error, treating as success:', responseData);
          return {
            success: true,
            data: responseData,
            message: 'Permission created successfully',
            statusCode: 201
          };
        }
      }
      
      let errorMessage = 'Error creating permission';
      
      if (error.response?.status === 422) {
        console.error('🔧 422 Validation Error Details:');
        console.error('🔧 - Status:', error.response.data?.status);
        console.error('🔧 - Message:', error.response.data?.message);
        console.error('🔧 - Errors:', error.response.data?.errors);
        
        errorMessage = 'Validation error - Please check your input data';
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors.map((err: any) => {
            console.error('🔧 Processing error:', err);
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            if (err.field && err.message) return `${err.field}: ${err.message}`;
            return err.msg || err.message || JSON.stringify(err);
          });
          errorMessage = validationErrors.join(', ');
          console.error('🔧 Final validation errors:', validationErrors);
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 409) {
        errorMessage = 'Permission name already exists';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Update permission
  async updatePermission(id: number, data: UpdatePermissionRequest): Promise<ApiResponse<Permission>> {
    try {
      console.log('🔧 PermissionsService.updatePermission called:', { id, data });
      
      const response = await apiClient.put<Permission>(`${this.baseUrl}/${id}`, data);
      
      console.log('🔧 PermissionsService.updatePermission response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PermissionsService.updatePermission error:', error);
      
      let errorMessage = 'Error updating permission';
      
      if (error.response?.status === 422) {
        errorMessage = 'Validation error - Please check your input data';
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            return err.msg || err.message || 'Validation error';
          });
          errorMessage = validationErrors.join(', ');
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Permission not found';
      } else if (error.response?.status === 409) {
        errorMessage = 'Permission name already exists';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Delete permission
  async deletePermission(id: number): Promise<ApiResponse<any>> {
    try {
      console.log('🔧 PermissionsService.deletePermission called:', { id });
      
      const response = await apiClient.delete<any>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 PermissionsService.deletePermission response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PermissionsService.deletePermission error:', error);
      
      let errorMessage = 'Error deleting permission';
      
      if (error.response?.status === 404) {
        errorMessage = 'Permission not found';
      } else if (error.response?.status === 409) {
        errorMessage = 'Cannot delete permission - it is being used by roles';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Toggle permission status
  async togglePermissionStatus(id: number): Promise<ApiResponse<Permission>> {
    try {
      console.log('🔧 PermissionsService.togglePermissionStatus called:', { id });
      
      const response = await apiClient.patch<Permission>(`${this.baseUrl}/${id}/toggle-status`);
      
      console.log('🔧 PermissionsService.togglePermissionStatus response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PermissionsService.togglePermissionStatus error:', error);
      
      let errorMessage = 'Error toggling permission status';
      
      if (error.response?.status === 404) {
        errorMessage = 'Permission not found';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Calculate stats from permissions data
  calculateStats(permissions: Permission[]): PermissionStats {
    const resources = [...new Set(permissions.map(p => p.resource))];
    const actions = [...new Set(permissions.map(p => p.action))];
    
    return {
      total_permissions: permissions.length,
      active_permissions: permissions.filter(permission => permission.isActive).length,
      inactive_permissions: permissions.filter(permission => !permission.isActive).length,
      resources,
      actions,
    };
  }
}

export const permissionsService = new PermissionsService();
